import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import { InfoOutlined } from '@mui/icons-material';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import {
  CTR_CLICKABLE_NOTE,
  DAYPART_BUCKETS,
  DAYPART_DEFINITIONS,
  compactCurrency,
  compactNum,
  currency,
  daypartBucket,
  daypartRange,
  num,
  percent,
  percentWhole,
} from '../format';
import { useExportWidgetCsv } from '../hooks/useExportWidgetCsv';
import { useReportWidget } from '../hooks/useReportWidget';
import BarChart from './BarChart';
import MetricToggle from './MetricToggle';
import WidgetCard from './WidgetCard';
import WidgetExportButton from './WidgetExportButton';

const KEY = 'daypartMetric';

const METRIC_OPTIONS = [
  { key: 'impressions', label: 'Impressions' },
  { key: 'ctr', label: 'CTR', note: CTR_CLICKABLE_NOTE },
  { key: 'vcr', label: 'VCR' },
];

const formatValue =
  (metric: string) =>
  (value: unknown): string => {
    if (metric === 'spend') return currency(value);
    // VCR drops decimals (prep #0000 — "can it just be 85%?"); CTR keeps its own.
    if (metric === 'vcr') return percentWhole(value);
    if (metric === 'ctr') return percent(value);
    return num(value);
  };

/** Axis ticks in the compact form; the tooltip keeps full precision. */
const formatTick =
  (metric: string) =>
  (value: unknown): string => {
    if (metric === 'spend') return compactCurrency(value);
    if (metric === 'vcr') return percentWhole(value);
    if (metric === 'ctr') return percent(value);
    return compactNum(value);
  };

/**
 * Builds the export's rows from the chart's own bucketed labels/values
 * (flank#4838), rather than the raw per-hour rows the widget fetched — so the
 * CSV/XLSX shows the same 6 named dayparts the chart renders instead of 24
 * unbucketed, unlabelled hour-of-day integers.
 */
export const buildDaypartExportRows = (labels: readonly string[], values: readonly number[], metric: string) =>
  labels.map((label, i) => ({ daypart: label, [metric]: values[i] }));

/**
 * Aggregates raw hour-of-day rows into the platform's named dayparts (Late
 * Fringe … Primetime) for the given metric — pulled out of the component so
 * ExportDialog's page-level export can build the same bucketed rows from the
 * raw widget rows it reads out of the store, instead of re-exporting them
 * unbucketed (flank#4838: the per-widget export used this logic; the page
 * export didn't, and still showed 24 unlabelled hour_of_day integers).
 */
export const aggregateDaypartRows = (rows: readonly ReportRow[], metric: string) => {
  const isRate = metric === 'ctr' || metric === 'vcr';
  const sums = new Map<string, { total: number; weight: number }>();
  for (const bucket of DAYPART_BUCKETS) sums.set(bucket, { total: 0, weight: 0 });

  for (const row of rows) {
    const bucket = daypartBucket(row.hour_of_day);
    if (bucket === 'Unmapped') continue;
    const entry = sums.get(bucket) ?? { total: 0, weight: 0 };
    const value = Number(row[metric] ?? 0);
    if (!Number.isFinite(value)) continue;
    if (isRate) {
      // Impression-weighted: an hour with 12 impressions must not carry the
      // same weight as one with 12 million. Both branches were previously
      // identical, so CTR/VCR were a flat mean of hourly rates.
      const weight = Number(row.impressions ?? 0);
      if (!Number.isFinite(weight) || weight <= 0) continue;
      entry.total += value * weight;
      entry.weight += weight;
    } else {
      entry.total += value;
      entry.weight += 1;
    }
    sums.set(bucket, entry);
  }

  const labelsOut = [...DAYPART_BUCKETS];
  const valuesOut = labelsOut.map(label => {
    const entry = sums.get(label)!;
    if (!entry.weight) return 0;
    return isRate ? entry.total / entry.weight : entry.total;
  });
  return { labels: labelsOut, values: valuesOut };
};

/** Aggregate hour-of-day rows into the platform's named dayparts (Late Fringe … Primetime). */
const DaypartCard = () => {
  const [metric, setMetric] = useState('impressions');
  const isRate = metric === 'ctr' || metric === 'vcr';
  const { rows, loading, error } = useReportWidget(KEY, {
    dimensions: ['hour_of_day'],
    // Rates must be re-aggregated by impression volume, so pull impressions
    // alongside them as the weight.
    metrics: isRate ? [metric, 'impressions'] : [metric],
    sort: [{ field: 'hour_of_day', dir: 'asc' }],
    limit: 24,
  });

  const { labels, values } = useMemo(() => aggregateDaypartRows(rows, metric), [rows, metric]);

  const exportRows = useMemo(() => buildDaypartExportRows(labels, values, metric), [labels, values, metric]);
  const onExportCsv = useExportWidgetCsv('daypartMetric', exportRows);

  return (
    // Title and toggle go through WidgetCard's own header rather than children:
    // children are replaced wholesale by the loading / error / empty branch, so
    // an empty range previously rendered an untitled box with no way to switch
    // metric -- exactly when you'd want to try another one.
    <WidgetCard
      title='Performance by Daypart'
      titleAdornment={
        /*
          Next to the title, not in headerAction: the far-right slot on a
          half-width card can land outside the viewport, and a help icon nobody
          can find isn't help. Earl looked "next to the title" and found nothing
          (FB-003).
        */
        <Tooltip
          arrow
          placement='top'
          title={
            <Stack spacing={0.25} sx={{ py: 0.5 }}>
              {DAYPART_DEFINITIONS.map(({ daypart, range }) => (
                <Typography key={daypart} variant='caption' sx={{ whiteSpace: 'nowrap' }}>
                  <strong>{daypart}</strong> — {range}
                </Typography>
              ))}
            </Stack>
          }
        >
          {/*
            An IconButton, not a bare SvgIcon: an SVG can't take keyboard focus,
            so the tooltip was mouse-only and unreachable by tab. The ranges also
            print under each bar, so this is the secondary route either way.
          */}
          <IconButton
            aria-label='Daypart time ranges'
            size='small'
            disableRipple
            sx={{ p: 0.25, color: 'text.secondary', cursor: 'help', displayPrint: 'none' }}
          >
            <InfoOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      }
      headerAction={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <MetricToggle options={METRIC_OPTIONS} value={metric} onChange={setMetric} />
          <WidgetExportButton onExport={onExportCsv} disabled={rows.length === 0} />
        </Box>
      }
      loading={loading}
      error={error}
      isEmpty={rows.length === 0}
    >
      {/*
        The range sits under each bar, not only behind the ⓘ. A help icon is
        easy to miss — Earl couldn't find it at all on his monitor — and hover
        doesn't exist in an export or a printed PDF. Same reasoning as the
        labelled daypart values in Custom Dashboards.
      */}
      <BarChart
        labels={labels}
        values={values}
        tickSubLabels={labels.map(daypartRange)}
        format={formatValue(metric)}
        tickFormat={formatTick(metric)}
      />
    </WidgetCard>
  );
};

export default DaypartCard;
